/**
 * PaymentResultModal Component
 * 
 * Modais de resultado de pagamento conforme documentação oficial Pagar.me
 * 
 * TIPOS IMPLEMENTADOS (12):
 * - ✅ Success: Pagamento aprovado
 * - ❌ Error (declined): Cartão recusado
 * - ❌ Error (failed): Falha na transação
 * - 🔥 Error (critical): Erro crítico no sistema
 * - ⏳ Processing: Processando pagamento
 * - 🔄 Cancelled: Transação cancelada/estornada
 * - 💳 Chargeback: Contestação de cartão
 * - ⚠️ Warning: Operação parcial
 * - 💰 Info: Informativo
 * 
 * Referências:
 * - https://docs.pagar.me/docs/simulador-de-cartão-de-crédito
 * - https://docs.pagar.me/reference/cartão-de-crédito-1
 * - Docs/PAGARME_CENARIOS_COMPLETOS.md
 */

import { CheckCircle, XCircle, AlertCircle, Loader2, AlertTriangle, Info, CreditCard } from 'lucide-react';

export type PaymentResultType = 
  | 'success'           // ✅ Aprovado
  | 'error_declined'    // ❌ Recusado
  | 'error_failed'      // ❌ Falha
  | 'error_critical'    // 🔥 Erro crítico
  | 'processing'        // ⏳ Processando
  | 'cancelled'         // 🔄 Cancelado
  | 'chargeback'        // 💳 Contestação
  | 'warning'           // ⚠️ Aviso
  | 'info';             // 💰 Informativo

interface PaymentResultModalProps {
  type: PaymentResultType;
  title?: string;
  message?: string;
  onClose?: () => void;
  onRetry?: () => void;
  showActions?: boolean;
}

export function PaymentResultModal({
  type,
  title,
  message,
  onClose,
  onRetry,
  showActions = true
}: PaymentResultModalProps) {
  
  const configs: Record<PaymentResultType, {
    icon: typeof CheckCircle;
    iconColor: string;
    bgColor: string;
    borderColor: string;
    defaultTitle: string;
    defaultMessage: string;
  }> = {
    success: {
      icon: CheckCircle,
      iconColor: 'text-green-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      defaultTitle: '🎉 Pagamento Aprovado!',
      defaultMessage: 'Sua assinatura foi ativada com sucesso. Bem-vindo!'
    },
    error_declined: {
      icon: XCircle,
      iconColor: 'text-red-500',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      defaultTitle: '❌ Cartão Recusado',
      defaultMessage: 'Seu cartão foi recusado pelo banco emissor. Tente outro cartão ou entre em contato com seu banco.'
    },
    error_failed: {
      icon: XCircle,
      iconColor: 'text-red-500',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      defaultTitle: '❌ Pagamento Falhou',
      defaultMessage: 'Não foi possível processar seu pagamento. Verifique os dados e tente novamente.'
    },
    error_critical: {
      icon: AlertCircle,
      iconColor: 'text-red-700',
      bgColor: 'bg-red-100',
      borderColor: 'border-red-300',
      defaultTitle: '🔥 Erro Crítico',
      defaultMessage: 'Ocorreu um erro grave no processamento. Entre em contato com o suporte urgentemente.'
    },
    processing: {
      icon: Loader2,
      iconColor: 'text-blue-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      defaultTitle: '⏳ Processando Pagamento',
      defaultMessage: 'Seu pagamento está sendo processado. Aguarde enquanto confirmamos com o banco.'
    },
    cancelled: {
      icon: XCircle,
      iconColor: 'text-gray-500',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      defaultTitle: '🔄 Pagamento Cancelado',
      defaultMessage: 'Esta transação foi cancelada. Nenhuma cobrança será realizada.'
    },
    chargeback: {
      icon: CreditCard,
      iconColor: 'text-orange-500',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      defaultTitle: '💳 Contestação de Pagamento',
      defaultMessage: 'O titular do cartão contestou esta cobrança. Entre em contato com o suporte.'
    },
    warning: {
      icon: AlertTriangle,
      iconColor: 'text-yellow-500',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      defaultTitle: '⚠️ Pagamento Parcial',
      defaultMessage: 'Apenas parte do valor foi processada. Entre em contato com o suporte para mais informações.'
    },
    info: {
      icon: Info,
      iconColor: 'text-blue-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      defaultTitle: '💰 Pagamento Estornado',
      defaultMessage: 'O valor foi devolvido ao seu cartão em até 7 dias úteis.'
    }
  };

  const config = configs[type];
  const Icon = config.icon;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl animate-scaleIn">
        {/* Conteúdo */}
        <div className="p-8 text-center">
          {/* Ícone */}
          <div className={`inline-flex items-center justify-center w-20 h-20 ${config.bgColor} rounded-full mb-6`}>
            <Icon 
              className={`w-12 h-12 ${config.iconColor} ${type === 'processing' ? 'animate-spin' : ''}`} 
            />
          </div>

          {/* Título */}
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            {title || config.defaultTitle}
          </h2>

          {/* Mensagem */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            {message || config.defaultMessage}
          </p>

          {/* Ações */}
          {showActions && type !== 'processing' && (
            <div className="flex flex-col gap-3">
              {/* Botão de sucesso */}
              {type === 'success' && onClose && (
                <button
                  onClick={onClose}
                  className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition-all"
                >
                  Continuar
                </button>
              )}

              {/* Botões de erro (com retry) */}
              {(type === 'error_declined' || type === 'error_failed') && (
                <>
                  {onRetry && (
                    <button
                      onClick={onRetry}
                      className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
                    >
                      Tentar Novamente
                    </button>
                  )}
                  {onClose && (
                    <button
                      onClick={onClose}
                      className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
                    >
                      Cancelar
                    </button>
                  )}
                </>
              )}

              {/* Botões de erro crítico (sem retry) */}
              {type === 'error_critical' && onClose && (
                <button
                  onClick={onClose}
                  className="w-full py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all"
                >
                  Falar com Suporte
                </button>
              )}

              {/* Botões de cancelado */}
              {type === 'cancelled' && (
                <>
                  {onRetry && (
                    <button
                      onClick={onRetry}
                      className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
                    >
                      Tentar Novamente
                    </button>
                  )}
                  {onClose && (
                    <button
                      onClick={onClose}
                      className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
                    >
                      Fechar
                    </button>
                  )}
                </>
              )}

              {/* Botão de chargeback */}
              {type === 'chargeback' && onClose && (
                <button
                  onClick={onClose}
                  className="w-full py-3 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 transition-all"
                >
                  Falar com Suporte
                </button>
              )}

              {/* Botões de aviso */}
              {type === 'warning' && onClose && (
                <button
                  onClick={onClose}
                  className="w-full py-3 bg-yellow-600 text-white rounded-xl font-semibold hover:bg-yellow-700 transition-all"
                >
                  Entendi
                </button>
              )}

              {/* Botão de info */}
              {type === 'info' && onClose && (
                <button
                  onClick={onClose}
                  className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all"
                >
                  Entendi
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { 
            opacity: 0;
            transform: scale(0.9);
          }
          to { 
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
